import HulahoopLogo from "@/assets/hulahoop-logo-pos.svg";
import { cn } from "@/utils";
import { useTranslation } from "react-i18next";

interface LoadingScreenProps extends React.HTMLAttributes<HTMLDivElement> {
	message?: string;
	showLogo?: boolean;
	variant?: "default" | "minimal" | "branded";
	size?: "sm" | "md" | "lg";
}

export const LoadingScreen = ({
	className,
	message,
	showLogo = true,
	variant = "default",
	size = "md",
	...props
}: LoadingScreenProps) => {
	const { t } = useTranslation();

	const logoSizes = {
		sm: "h-8",
		md: "h-12",
		lg: "h-16",
	};

	const spinnerSizes = {
		sm: "w-8 h-8",
		md: "w-12 h-12",
		lg: "w-16 h-16",
	};

	const variants = {
		default: "min-h-screen bg-app",
		minimal: "min-h-screen bg-bg-primary",
		branded: "min-h-screen bg-gradient-to-br from-bg-secondary to-bg-tertiary",
	};

	return (
		<div
			className={cn(
				"flex flex-col items-center justify-center",
				variants[variant],
				className,
			)}
			{...props}
		>
			<div className="flex flex-col items-center space-y-6">
				{showLogo && (
					<div className="flex justify-center items-center">
						<img
							src={HulahoopLogo}
							alt="Hulahoop"
							className={cn(logoSizes[size], "w-auto opacity-90 animate-pulse")}
							style={{
								filter:
									"drop-shadow(0 4px 12px color-mix(in srgb, var(--color-primary) 15%, transparent))",
							}}
						/>
					</div>
				)}

				<div className="flex justify-center items-center">
					<div className="relative">
						<div
							className={cn(
								spinnerSizes[size],
								"rounded-full border-4 opacity-30",
							)}
							style={{
								borderColor: "var(--color-border-secondary)",
							}}
						/>
						<div
							className={cn(
								"absolute top-0 left-0",
								spinnerSizes[size],
								"rounded-full border-4 border-transparent animate-spin",
							)}
							style={{
								borderTopColor: "var(--color-primary)",
								borderRightColor:
									"color-mix(in srgb, var(--color-primary) 30%, transparent)",
								animationDuration: "1s",
								animationTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
							}}
						/>
					</div>
				</div>
			</div>

			<div
				role="status"
				aria-live="polite"
				aria-label={message || t("loading", "Cargando...")}
				className="sr-only"
			>
				{t("loading.status", "Cargando contenido, por favor espera")}
			</div>
		</div>
	);
};
